import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { reconcileBet } from "services/reportsService";
import { ReconcileAction, ReconcileMethod, ReconcileResolvedAs } from "utils/reconcileConstants";
import { useSnackbar } from "hooks/useSnackbar";

const STATUS_ACTION_MAP: Record<string, { allowCredit: boolean; allowRefund: boolean; warning?: string }> = {
  debit_completed: { allowCredit: true, allowRefund: true },
  credit_failed: { allowCredit: true, allowRefund: true },
  refund_failed: { allowCredit: false, allowRefund: true },
  credit_started: { allowCredit: true, allowRefund: false, warning: "Bet is in credit_started state. A previous credit attempt may be in progress. Risk of duplicate credit." },
  credit_completed: { allowCredit: true, allowRefund: false, warning: "Bet is already credit_completed. Proceeding will risk a double credit." },
  refund_started: { allowCredit: false, allowRefund: true, warning: "Bet is in refund_started state. A previous refund attempt may be in progress. Risk of duplicate refund." },
  refund_completed: { allowCredit: false, allowRefund: true, warning: "Bet is already refund_completed. Proceeding will risk a double refund." },
  debit_failed: { allowCredit: false, allowRefund: true, warning: "Bet status is debit_failed. The bet may not exist on the platform. Proceed only if you are certain the debit was processed." },
};

interface ReconcileDialogProps {
  open: boolean;
  onClose: () => void;
  betTxnId: string;
  status: string;
  betAmount: number;
  winAmount: number;
  currency: string;
  onSuccess: () => void;
}

export default function ReconcileDialog({
  open,
  onClose,
  betTxnId,
  status,
  betAmount,
  winAmount,
  currency,
  onSuccess,
}: ReconcileDialogProps) {
  const { showSnackbar } = useSnackbar();
  const statusConfig = STATUS_ACTION_MAP[status] ?? { allowCredit: false, allowRefund: false };

  const defaultAction = statusConfig.allowCredit ? ReconcileAction.CREDIT : ReconcileAction.REFUND;
  const [method, setMethod] = useState<ReconcileMethod>(ReconcileMethod.NORMAL);
  const [action, setAction] = useState<ReconcileAction>(defaultAction);
  const [amount, setAmount] = useState(winAmount > 0 ? String(winAmount) : "");
  const [resolvedAs, setResolvedAs] = useState<ReconcileResolvedAs>(ReconcileResolvedAs.WON);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const newDefault = statusConfig.allowCredit ? ReconcileAction.CREDIT : ReconcileAction.REFUND;
      setAction(newDefault);
      setAmount(winAmount > 0 ? String(winAmount) : "");
      setResolvedAs(ReconcileResolvedAs.WON);
      setMethod(ReconcileMethod.NORMAL);
    }
  }, [open, status, winAmount]);

  const isWarning = !!statusConfig.warning;
  const showCreditFields = action === ReconcileAction.CREDIT;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload: any = { betTxnId, action, method };
      if (action === ReconcileAction.CREDIT) {
        payload.winAmount = Number(amount);
        payload.resolvedAs = resolvedAs;
      } else {
        payload.resolvedAs = ReconcileResolvedAs.VOID;
      }
      const resp = await reconcileBet(payload);
      if (resp?.data?.success) {
        showSnackbar("Reconciliation successful", "success");
        onSuccess();
        onClose();
      } else {
        const errMsg = resp?.data?.errorDetails?.message ?? "Reconciliation failed";
        showSnackbar(errMsg, "error");
      }
    } catch (error: any) {
      showSnackbar(error?.message ?? "Reconciliation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manual Reconcile</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Bet Txn ID: <strong>{betTxnId}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current Status: <strong>{status}</strong>
          </Typography>
        </Box>

        {isWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {statusConfig.warning}
          </Alert>
        )}

        <Divider sx={{ mb: 2 }} />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Method</InputLabel>
          <Select
            value={method}
            label="Method"
            onChange={(e) => setMethod(e.target.value as any)}
          >
            <MenuItem value={ReconcileMethod.NORMAL}>Normal</MenuItem>
            <MenuItem value={ReconcileMethod.PLATFORM_ENDPOINT}>Platform Endpoint</MenuItem>
          </Select>
        </FormControl>

        {(statusConfig.allowCredit || statusConfig.allowRefund) && (
          <FormControl sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
              Action
            </Typography>
            <RadioGroup
              row
              value={action}
              onChange={(e) => setAction(e.target.value as any)}
            >
              {statusConfig.allowCredit && (
                <FormControlLabel value={ReconcileAction.CREDIT} control={<Radio />} label="Credit" />
              )}
              {statusConfig.allowRefund && (
                <FormControlLabel value={ReconcileAction.REFUND} control={<Radio />} label="Refund" />
              )}
            </RadioGroup>
          </FormControl>
        )}

        {showCreditFields ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={`Amount (${currency})`}
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, step: "any" }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Resolved As</InputLabel>
              <Select
                value={resolvedAs}
                label="Resolved As"
                onChange={(e) => setResolvedAs(e.target.value as any)}
              >
                <MenuItem value={ReconcileResolvedAs.WON}>Won</MenuItem>
                <MenuItem value={ReconcileResolvedAs.LOST}>Lost</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2">
              Refund amount (fixed): <strong>{betAmount} {currency}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Resolved as: <strong>void</strong>
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSubmit}
          disabled={loading || (showCreditFields && !amount)}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Processing..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
